import { useState } from "react";
import { yerelGunBasi } from "@koclukpanel/contracts";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Alan,
  BosDurum,
  Buton,
  Iskelet,
  Kart,
  Secim,
  Sekmeler,
  cn,
  anMetni,
} from "@koclukpanel/ui";
import {
  ApiHatasi,
  adayDurumDegistir,
  adayEtkinligiEkle,
  adayGetir,
  adayOlustur,
  adaylariGetir,
  benGetir,
  gorusmePlanla,
  huniRaporu,
  gorusmeUzmanlari,
  type AdayAyrinti,
  type AdayDurumu,
  type AdayKaynagi,
  type AdayOlusturmaSonucu,
  type HuniRaporu,
  type KayipNedeni,
  type Maliyet,
} from "./api";
import { adayDavetKoduGir } from "./api-oyun2";
import { izinVarMi } from "./izin";
import { ReklamHarcamasi } from "./KucukFormlar";

/**
 * ADAY HUNISI — F-CRM
 *
 * Faz 5'in kabul olcutu: "Basvurudan kayda tum aday sureci panelde
 * yuruyor."
 *
 * ================================================================
 * OLCULMEDI ILE SIFIR AYNI GORUNMEMELI
 * ================================================================
 * Sunucu kayit basina maliyeti `{ olculdu: false }` seklinde
 * donebiliyor: harcama girilmemisse maliyet SIFIR DEGIL BILINMIYOR.
 * Ekran ikisini ayni gosterseydi — "0 ₺" — harcamasi girilmemis kanal
 * "bedava" gorunur ve butce yanlis yere kaydirilirdi. Bu ekranda
 * olculmemis her sayi "—" ve altinda sebebi yaziyor.
 */

const DURUM_ADI: Record<AdayDurumu, string> = {
  NEW: "Yeni",
  TO_CALL: "Aranacak",
  UNREACHED: "Ulaşılamadı",
  CONTACTED: "Görüşüldü",
  INFORMED: "Bilgilendirildi",
  MEETING_SCHEDULED: "Tanışma planlandı",
  MEETING_HELD: "Tanışma yapıldı",
  OFFER_MADE: "Teklif verildi",
  FOLLOW_UP: "Takipte",
  ENROLLED: "Kaydoldu",
  LOST: "Kaybedildi",
};

const KAYNAK_ADI: Record<AdayKaynagi, string> = {
  GOOGLE_ADS: "Google Ads",
  META_ADS: "Meta Ads",
  ORGANIC: "Organik",
  INSTAGRAM: "Instagram",
  WHATSAPP: "WhatsApp",
  REFERRAL: "Tavsiye",
  PHONE: "Telefon",
  MANUAL: "Elle",
  OTHER: "Diğer",
};

const NEDEN_ADI: Record<KayipNedeni, string> = {
  PRICE: "Fiyat",
  POSTPONED: "Erteledi",
  UNREACHABLE: "Ulaşılamadı",
  CHOSE_COMPETITOR: "Rakibi seçti",
  STUDENT_REFUSED: "Öğrenci istemedi",
  PARENT_REFUSED: "Veli istemedi",
  NOT_SUITABLE: "Uygun değil",
  WRONG_LEAD: "Hatalı kayıt",
  OTHER: "Diğer",
};

const BASAMAK_ADI: Record<string, string> = {
  NEW: "Kayıt geldi",
  CONTACTED: "Temas kuruldu",
  INFORMED: "Bilgilendirildi",
  MEETING_SCHEDULED: "Tanışma planlandı",
  MEETING_HELD: "Tanışma yapıldı",
  OFFER_MADE: "Teklif verildi",
  ENROLLED: "Kaydoldu",
};

/**
 * Oran biçimi.
 *
 * `null` → "—" (ölçülemedi), `0` → "%0" (kimse geçemedi). İkisi ayrı
 * şey söylüyor ve ekranda da ayrı görünmeli.
 *
 * Ondalık YALNIZCA küçük ama sıfır olmayan oranlarda: %3,2 ile %3
 * arasındaki fark anlamlı, ama tam sıfırı "%0,0" yazmak ölçülmüş
 * gibi bir kesinlik izlenimi verir.
 */
function oran(deger: number | null): string {
  if (deger === null) return "—";
  if (deger === 0) return "%0";
  return `%${(deger * 100).toFixed(deger < 0.1 ? 1 : 0)}`;
}

function para(kurus: number): string {
  return kurus.toLocaleString("tr-TR", { maximumFractionDigits: 0 }) + " ₺";
}

export function Adaylar() {
  const [gorunum, setGorunum] = useState<"liste" | "huni">("liste");

  return (
    <div className="flex flex-col gap-4">
      {/*
        SEKME DEĞİL, SÜZGEÇ DÜĞMELERİ — fark ekran okuyucuda.

        Elle yazılmış `role="tab"` şeridi vardı: `aria-selected` koyulmuş
        ama ok tuşlarıyla gezinme, gezinen odak ve `tabpanel` bağı YOKTU.
        Eksik kurulmuş bir ARIA deseni hiç yazmamaktan KÖTÜ — kullanıcıya
        "burada ok tuşlarıyla gezebilirsin" diye söz verip tutmuyor.

        `Sekmeler` `aria-pressed` kullanıyor: aynı listeyi süzen
        basılı-kalan düğmeler, ki gerçekte olan bu.
      */}
      <Sekmeler
        etiket="Aday görünümü"
        secili={gorunum}
        onSec={setGorunum}
        sekmeler={[
          { deger: "liste", etiket: "Adaylar" },
          { deger: "huni", etiket: "Huni ve kaynaklar" },
        ]}
      />

      {gorunum === "liste" ? (
        <AdayListesi />
      ) : (
        <div className="flex flex-col gap-4">
          {/*
            HARCAMA GIRISI HUNININ YANINDA: huni "kayit basina maliyet"
            gosteriyor ve o sutunun girdisi bu form. Ayri bir yerde
            olsaydi, maliyetin neden hesaplanmadigini goren kisi
            duzeltmenin nerede oldugunu bilemezdi.
          */}
          <ReklamHarcamasi />
          <HuniGorunumu />
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------
// LISTE
// ---------------------------------------------------------------

function AdayListesi() {
  const [durum, setDurum] = useState<AdayDurumu | "">("");
  const [acikAday, setAcikAday] = useState<string | null>(null);
  const [formAcik, setFormAcik] = useState(false);

  const { data, isPending, isError, error, refetch } = useQuery({
    queryKey: ["adaylar", durum],
    queryFn: () => adaylariGetir(durum ? { durum } : {}),
  });

  if (isPending) return <Iskelet satir={4} />;

  if (isError) {
    return (
      <BosDurum
        ton="hata"
        baslik="Adaylar alınamadı"
        aciklama={error instanceof ApiHatasi ? error.message : undefined}
        eylem={
          <Buton gorunum="ikincil" onClick={() => void refetch()}>
            Tekrar dene
          </Buton>
        }
      />
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-end gap-3">
        <Secim
          etiket="Durum"
          value={durum}
          onChange={(e) => setDurum(e.target.value as AdayDurumu | "")}
        >
          <option value="">Hepsi</option>
          {(Object.keys(DURUM_ADI) as AdayDurumu[]).map((d) => (
            <option key={d} value={d}>
              {DURUM_ADI[d]}
            </option>
          ))}
        </Secim>

        <Buton onClick={() => setFormAcik((a) => !a)} aria-expanded={formAcik}>
          {formAcik ? "Formu kapat" : "Yeni aday"}
        </Buton>
      </div>

      {formAcik && <YeniAdayFormu onKapat={() => setFormAcik(false)} />}

      {data.adaylar.length === 0 ? (
        <BosDurum
          baslik="Aday yok"
          aciklama={
            durum
              ? `"${DURUM_ADI[durum]}" durumunda aday bulunmuyor.`
              : "Henüz aday kaydı açılmamış."
          }
        />
      ) : (
        <ul className="flex flex-col gap-2">
          {data.adaylar.map((a) => (
            <li key={a.id}>
              <button
                type="button"
                onClick={() => setAcikAday(acikAday === a.id ? null : a.id)}
                aria-expanded={acikAday === a.id}
                className={cn(
                  "min-h-dokunma rounded-alan flex w-full items-center gap-3 px-3 text-left",
                  "bg-notr-50 hover:bg-notr-100",
                  "focus-visible:outline-marka-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2",
                )}
              >
                <span className="min-w-0 flex-1">
                  <span className="text-notr-900 block truncate text-sm font-medium">
                    {a.studentName}
                  </span>
                  <span className="text-notr-600 block text-xs">
                    {KAYNAK_ADI[a.source]} · {new Date(a.createdAt).toLocaleDateString("tr-TR")}
                    {a.nextFollowUpAt
                      ? ` · takip ${new Date(a.nextFollowUpAt).toLocaleDateString("tr-TR")}`
                      : ""}
                  </span>
                </span>
                <span className="bg-notr-0 text-notr-700 ring-notr-200 shrink-0 rounded-full px-2 py-0.5 text-xs font-medium ring-1">
                  {DURUM_ADI[a.status]}
                </span>
              </button>

              {acikAday === a.id && <AdayPaneli adayId={a.id} />}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ---------------------------------------------------------------
// TEK ADAY
// ---------------------------------------------------------------

function AdayPaneli({ adayId }: { adayId: string }) {
  const istemci = useQueryClient();
  const [hedef, setHedef] = useState<AdayDurumu | "">("");
  const [neden, setNeden] = useState<KayipNedeni | "">("");
  const [not, setNot] = useState("");

  const { data, isPending, isError, error } = useQuery<AdayAyrinti>({
    queryKey: ["aday", adayId],
    queryFn: () => adayGetir(adayId),
  });

  const durumMut = useMutation({
    mutationFn: () =>
      adayDurumDegistir(adayId, hedef as AdayDurumu, {
        lostReason: hedef === "LOST" ? (neden as KayipNedeni) : null,
        not: not.trim() || null,
      }),
    onSuccess: async () => {
      setHedef("");
      setNeden("");
      setNot("");
      await istemci.invalidateQueries({ queryKey: ["aday", adayId] });
      await istemci.invalidateQueries({ queryKey: ["adaylar"] });
    },
  });

  if (isPending) return <Iskelet satir={4} />;
  if (isError) {
    return (
      <p role="alert" className="text-hata-500 px-3 py-2 text-sm">
        {error instanceof ApiHatasi ? error.message : "Aday alınamadı."}
      </p>
    );
  }

  // Kaybedildi seçildiyse neden ZORUNLU — sunucu da reddediyor ama
  // kullanıcıyı sunucuya gitmeden uyarmak daha iyi.
  const nedenEksik = hedef === "LOST" && neden === "";

  return (
    <Kart className="mt-2 flex flex-col gap-4">
      <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
        <Satir etiket="Öğrenci" deger={data.studentName} />
        <Satir etiket="Telefon" deger={data.studentPhone ?? data.parentPhone ?? "—"} />
        <Satir etiket="Veli" deger={data.parentName ?? "—"} />
        <Satir etiket="Kaynak" deger={KAYNAK_ADI[data.source]} />
        <Satir etiket="Huni basamağı" deger={BASAMAK_ADI[data.basamak] ?? data.basamak} />
        <Satir etiket="Teklif" deger={data.offeredAmount ? `${data.offeredAmount} ₺` : "—"} />
      </dl>

      {data.status === "LOST" && data.lostReason && (
        <p className="rounded-alan bg-notr-50 text-notr-700 p-3 text-sm">
          <strong>Kaybedilme nedeni:</strong> {NEDEN_ADI[data.lostReason]}
          {data.lostNote ? ` — ${data.lostNote}` : ""}
        </p>
      )}

      {/*
        DUGMELER SUNUCUDAN GELEN LISTEDEN URETILIYOR.
        Burada sabit bir gecis tablosu tutulsaydi kural degistiginde
        ekran sessizce eskir ve kullaniciya calismayan dugmeler
        gosterirdi.
      */}
      {data.izinliGecisler.length === 0 ? (
        <p className="text-notr-600 text-sm">Bu aday kapandı; durum değiştirilemez.</p>
      ) : (
        <fieldset className="flex flex-col gap-3">
          <legend className="text-notr-900 text-sm font-semibold">Durumu değiştir</legend>

          <div className="flex flex-wrap gap-2">
            {data.izinliGecisler.map((g) => (
              <button
                key={g}
                type="button"
                onClick={() => setHedef(hedef === g ? "" : g)}
                aria-pressed={hedef === g}
                className={cn(
                  "min-h-dokunma rounded-alan px-3 text-sm font-medium",
                  "focus-visible:outline-marka-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2",
                  hedef === g
                    ? "bg-marka-600 text-notr-0"
                    : "bg-notr-100 text-notr-700 hover:bg-notr-200",
                )}
              >
                {DURUM_ADI[g]}
              </button>
            ))}
          </div>

          {hedef === "LOST" && (
            <Secim
              etiket="Kaybedilme nedeni (zorunlu)"
              yardim="Neden yazılmadan aday kapatılamaz — “neden kaybediyoruz” sorusunun cevabı bu alandan çıkıyor."
              value={neden}
              onChange={(e) => setNeden(e.target.value as KayipNedeni | "")}
            >
              <option value="">Seçin…</option>
              {(Object.keys(NEDEN_ADI) as KayipNedeni[]).map((n) => (
                <option key={n} value={n}>
                  {NEDEN_ADI[n]}
                </option>
              ))}
            </Secim>
          )}

          {hedef && (
            <>
              <Alan
                etiket="Not (isteğe bağlı)"
                value={not}
                onChange={(e) => setNot(e.target.value)}
              />
              <div>
                <Buton
                  onClick={() => durumMut.mutate()}
                  disabled={nedenEksik || durumMut.isPending}
                >
                  {durumMut.isPending ? "Kaydediliyor…" : `"${DURUM_ADI[hedef]}" olarak işaretle`}
                </Buton>
              </div>
            </>
          )}

          {durumMut.isError && (
            <p role="alert" className="text-hata-500 text-sm">
              {durumMut.error instanceof ApiHatasi
                ? durumMut.error.message
                : "Durum değiştirilemedi."}
            </p>
          )}
        </fieldset>
      )}

      <AdayDavetKodu
        key={adayId}
        adayId={adayId}
        kayitliKod={data.davetKodu}
        kaydoldu={data.status === "ENROLLED"}
      />

      <GorusmePlanla adayId={adayId} />

      <EtkinlikBolumu adayId={adayId} etkinlikler={data.activities} />
    </Kart>
  );
}

/*
 * ADAY DAVET KODU — F-GAM-18 (oyunlaştırma dalga 2 / 7b).
 *
 * Satış personeli, adayın söylediği arkadaş davet kodunu aday kaydına girer; kod adayın kendisinden gelir,
 * öğrenciden arkadaş bilgisi hiç alınmaz. KOD DOĞRULAMASI (etkin kod, kendi kendini davet yok) SUNUCUDADIR: ekran
 * yalnız biçimi (8 karakter, büyük harf, boşluksuz) düzeltir ve sunucunun hata cümlesini OLDUĞU GİBİ gösterir —
 * kuralı burada yeniden yazmak, sunucuyla ayrışan ikinci bir kural olurdu.
 *
 * Kod bir kez girilince değişmez; aday kaydolduktan sonra da girilemez (sunucu zorlar, ekran formu hiç göstermez).
 * Yetki sunucudaki uçla AYNI izne bağlı (`crm.aday.yonet`); ekranın izin sorusu yalnız ipucudur, asıl kapı sunucuda.
 */
/** Boşlukları (görünmez karakterler dahil) atar, büyük harfe çevirir; `toUpperCase` dilden bağımsız ("i" → "I"). */
function davetKoduDuzelt(ham: string): string {
  return ham.replace(/[\s​-‍﻿]/g, "").toUpperCase();
}

const DAVET_KODU_UZUNLUGU = 8;

function AdayDavetKodu({
  adayId,
  kayitliKod,
  kaydoldu,
}: {
  adayId: string;
  kayitliKod: string | null;
  kaydoldu: boolean;
}) {
  const istemci = useQueryClient();
  const [girdi, setGirdi] = useState("");
  const [kaydedilen, setKaydedilen] = useState<string | null>(null);

  const ben = useQuery({ queryKey: ["ben"], queryFn: benGetir });
  // İzin sorgusu hata verirse formu GİZLEMİYORUZ: karar sunucudadır ve reddederse mesajı gösteririz.
  const yetkili = ben.isPending
    ? null
    : ben.data
      ? izinVarMi(ben.data.izinler, "crm.aday.yonet")
      : true;

  const mut = useMutation({
    mutationFn: () => adayDavetKoduGir(adayId, girdi),
    onSuccess: async (sonuc) => {
      setKaydedilen(sonuc.kod ?? girdi);
      setGirdi("");
      await istemci.invalidateQueries({ queryKey: ["aday", adayId] });
    },
  });

  const kod = kaydedilen ?? kayitliKod;

  return (
    <section className="border-notr-200 flex flex-col gap-2 border-t pt-4">
      <h4 className="text-notr-900 text-sm font-semibold">Davet kodu</h4>
      {kod ? (
        <p className="text-notr-900 text-sm">
          Davet kodu: <strong className="font-mono tracking-wider">{kod}</strong> — değiştirilemez
        </p>
      ) : kaydoldu ? (
        <p className="text-notr-600 text-sm">
          Davet kodu girilmemiş. Aday kaydolduğu için artık girilemez.
        </p>
      ) : yetkili === null ? (
        <Iskelet satir={1} />
      ) : yetkili === false ? (
        <p className="text-notr-600 text-sm">Davet kodu girilmemiş.</p>
      ) : (
        <form
          className="flex items-start gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            if (girdi.length === DAVET_KODU_UZUNLUGU && !mut.isPending) mut.mutate();
          }}
        >
          <div className="flex-1">
            <Alan
              etiket="Davet kodu"
              yardim="Adayın söylediği 8 karakterli kodu girin. Kaydedilince değiştirilemez."
              value={girdi}
              onChange={(e) => {
                setGirdi(davetKoduDuzelt(e.target.value).slice(0, DAVET_KODU_UZUNLUGU));
                if (mut.isError) mut.reset();
              }}
              maxLength={DAVET_KODU_UZUNLUGU}
              autoComplete="off"
              autoCapitalize="characters"
              spellCheck={false}
              className="font-mono tracking-wider"
              hata={
                mut.isError
                  ? mut.error instanceof ApiHatasi
                    ? mut.error.message
                    : "Davet kodu kaydedilemedi."
                  : undefined
              }
            />
          </div>
          <Buton
            type="submit"
            className="mt-6"
            disabled={girdi.length !== DAVET_KODU_UZUNLUGU || mut.isPending}
          >
            {mut.isPending ? "Kaydediliyor…" : "Kodu kaydet"}
          </Buton>
        </form>
      )}
    </section>
  );
}

function Satir({ etiket, deger }: { etiket: string; deger: string }) {
  return (
    <>
      <dt className="text-notr-600">{etiket}</dt>
      <dd className="text-notr-900 font-medium">{deger}</dd>
    </>
  );
}

function EtkinlikBolumu({
  adayId,
  etkinlikler,
}: {
  adayId: string;
  etkinlikler: AdayAyrinti["activities"];
}) {
  const istemci = useQueryClient();
  const [ozet, setOzet] = useState("");

  const mut = useMutation({
    mutationFn: () => adayEtkinligiEkle(adayId, { kind: "CALL", summary: ozet }),
    onSuccess: async () => {
      setOzet("");
      await istemci.invalidateQueries({ queryKey: ["aday", adayId] });
    },
  });

  return (
    <section className="border-notr-200 flex flex-col gap-3 border-t pt-4">
      <h4 className="text-notr-900 text-sm font-semibold">Görüşme geçmişi</h4>

      <form
        className="flex items-end gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          if (ozet.trim()) mut.mutate();
        }}
      >
        <div className="flex-1">
          <Alan
            etiket="Arama notu"
            value={ozet}
            onChange={(e) => setOzet(e.target.value)}
            placeholder="Kimle görüşüldü, ne konuşuldu"
          />
        </div>
        <Buton type="submit" disabled={!ozet.trim() || mut.isPending}>
          Ekle
        </Buton>
      </form>

      {etkinlikler.length === 0 ? (
        <p className="text-notr-600 text-sm">Henüz kayıt yok.</p>
      ) : (
        <ul className="flex flex-col gap-2">
          {etkinlikler.map((e) => (
            <li key={e.id} className="rounded-alan bg-notr-50 p-2 text-sm">
              <span className="text-notr-900">{e.summary}</span>
              <span className="text-notr-600 block text-xs">{anMetni(e.createdAt)}</span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

// ---------------------------------------------------------------
// YENI ADAY
// ---------------------------------------------------------------

function YeniAdayFormu({ onKapat }: { onKapat: () => void }) {
  const istemci = useQueryClient();
  const [ad, setAd] = useState("");
  const [telefon, setTelefon] = useState("");
  const [veliAdi, setVeliAdi] = useState("");
  const [kaynak, setKaynak] = useState<AdayKaynagi>("PHONE");
  const [sonuc, setSonuc] = useState<AdayOlusturmaSonucu | null>(null);

  const mut = useMutation({
    mutationFn: () =>
      adayOlustur({
        studentName: ad.trim(),
        studentPhone: telefon.trim() || null,
        parentName: veliAdi.trim() || null,
        source: kaynak,
      }),
    onSuccess: async (s) => {
      setSonuc(s);
      setAd("");
      setTelefon("");
      setVeliAdi("");
      await istemci.invalidateQueries({ queryKey: ["adaylar"] });
    },
  });

  return (
    <form
      className="rounded-kart border-notr-200 bg-notr-0 shadow-kart flex flex-col gap-3 border p-4"
      onSubmit={(e) => {
        e.preventDefault();
        if (ad.trim().length >= 2) mut.mutate();
      }}
    >
      <h3 className="text-notr-900 text-sm font-semibold">Yeni aday</h3>

      <Alan etiket="Öğrenci adı" value={ad} onChange={(e) => setAd(e.target.value)} required />
      <Alan
        etiket="Telefon"
        value={telefon}
        onChange={(e) => setTelefon(e.target.value)}
        inputMode="tel"
        yardim="Aynı numaralı yakın tarihli kayıt varsa uyarılırsınız; kayıt yine de açılır."
      />
      <Alan etiket="Veli adı" value={veliAdi} onChange={(e) => setVeliAdi(e.target.value)} />

      <Secim
        etiket="Kaynak"
        value={kaynak}
        onChange={(e) => setKaynak(e.target.value as AdayKaynagi)}
      >
        {(Object.keys(KAYNAK_ADI) as AdayKaynagi[]).map((k) => (
          <option key={k} value={k}>
            {KAYNAK_ADI[k]}
          </option>
        ))}
      </Secim>

      <div className="flex gap-2">
        <Buton type="submit" disabled={ad.trim().length < 2 || mut.isPending}>
          {mut.isPending ? "Kaydediliyor…" : "Kaydet"}
        </Buton>
        <Buton gorunum="sessiz" type="button" onClick={onKapat}>
          Kapat
        </Buton>
      </div>

      {mut.isError && (
        <p role="alert" className="text-hata-500 text-sm">
          {mut.error instanceof ApiHatasi ? mut.error.message : "Aday kaydedilemedi."}
        </p>
      )}

      {/*
        BENZER KAYIT UYARI, ENGEL DEGIL.
        Ayni veli iki cocugu icin ayri form doldurabilir; engellenseydi
        gercek bir aday kaybedilirdi. Ama sessizce gecilseydi iki satis
        danismani ayni kisiyi arardi.
      */}
      {sonuc && sonuc.benzerler.length > 0 && (
        <div role="status" className="rounded-alan bg-uyari-500/10 text-notr-800 p-3 text-sm">
          <strong>Kayıt açıldı.</strong> Aynı telefonla eşleşen {sonuc.benzerler.length} yakın
          tarihli kayıt var — aynı kişi olabilir:
          <ul className="mt-1 list-inside list-disc">
            {sonuc.benzerler.map((b) => (
              <li key={b.id}>
                {b.studentName} ({DURUM_ADI[b.status]})
              </li>
            ))}
          </ul>
        </div>
      )}

      {sonuc && sonuc.benzerler.length === 0 && (
        <p role="status" className="text-onay-700 text-sm">
          Kayıt açıldı.
        </p>
      )}
    </form>
  );
}

// ---------------------------------------------------------------
// HUNI RAPORU
// ---------------------------------------------------------------

/** Sunucunun GERÇEKTEN kullandığı aralık — ekran kendi sayısını yazmıyor. */
function araligiYaz(aralik: HuniRaporu["aralik"]): string {
  const g = (iso: string) => new Date(iso).toLocaleDateString("tr-TR");
  return `${g(aralik.baslangic)} – ${g(aralik.bitis)}`;
}

function HuniGorunumu() {
  const { data, isPending, isError, error, refetch } = useQuery<HuniRaporu>({
    queryKey: ["huni"],
    queryFn: () => huniRaporu(),
  });

  if (isPending) return <Iskelet satir={4} />;

  if (isError) {
    return (
      <BosDurum
        ton="hata"
        baslik="Rapor alınamadı"
        aciklama={error instanceof ApiHatasi ? error.message : undefined}
        eylem={
          <Buton gorunum="ikincil" onClick={() => void refetch()}>
            Tekrar dene
          </Buton>
        }
      />
    );
  }

  if (data.toplamAday === 0) {
    return (
      <BosDurum
        baslik="Bu aralıkta aday yok"
        aciklama={`Rapor ${araligiYaz(data.aralik)} arasını kapsıyor.`}
      />
    );
  }

  const enGenis = data.huni[0]?.ulasan ?? 1;

  return (
    <div className="flex flex-col gap-6">
      {/*
        ================================================================
        ARALIK SUNUCUDAN — VE DOLU DURUMDA DA YAZILIYOR
        ================================================================
        Burada "Rapor son 90 günü kapsıyor." elle yazılıydı ve yalnızca
        BOŞ durumda görünüyordu. İki ayrı sorun:

        1. `90` sunucudaki varsayılanın kopyasıydı
           (`crm.controller.ts:157`). Sunucu aralığı ZATEN gönderiyor
           (`aralik`) ve ekran onu hiç okumuyordu — bu deponun bugün
           üçüncü kez gördüğü desen: sunucu doğruyu yolluyor, ekran
           kopyasını yazıyor. Varsayılan değişse ekran eski sayıyı
           söylemeye devam ederdi ve hiçbir hata çıkmazdı. Bu depo bu
           hatayı daha önce yaşadı: "Bağlantı 7 gün geçerlidir"
           yazarken sunucu 3 gün uyguluyordu.

        2. Daha ağırı: aralık yalnızca aday YOKKEN yazılıyordu. Yani
           dönüşüm oranlarının okunduğu asıl ekranda, o oranların
           hangi dönemi kapsadığı HİÇ yazmıyordu. "%12 dönüşüm" bir
           haftanın mı bir yılın mı sonucu — okuyanın bilmesi gereken
           tek şey buydu.
      */}
      <p className="text-notr-600 text-xs">
        Rapor <strong className="font-medium">{araligiYaz(data.aralik)}</strong> arasını kapsıyor.
      </p>

      <Kart baslik="Huni">
        <p className="text-notr-600 mb-3 text-xs">
          Basamak, adayın <strong>durum alanından değil</strong> geri alınamayan olgulardan
          hesaplanır: kayıt tarihi, teklif tutarı, yapılan görüşme. Takipteki ama teklif almış aday
          da teklif basamağında sayılır.
        </p>

        {/*
          ================================================================
          ÖLÇÜLEMEYEN BASAMAK, ÖLÇÜLMÜŞ SIFIRDAN AYRI GÖRÜNÜYOR
          ================================================================
          `oran()` ikisini ayırıyordu — ama yalnızca en sağdaki hücrede
          ve yalnızca tek karakterle: `%0` yerine `—`. Çubuk ikisinde de
          aynıydı: boş gri bir yol.

          Yani "kimse geçemedi" ile "hesaplanamadı" ekranda **aynı
          resmi** çiziyordu ve fark, göz atarken görülmeyen tek bir
          çizgiye bırakılmıştı. `—` üstelik birçok ekran okuyucuda hiç
          seslendirilmiyor: o hücre kimi kullanıcı için tamamen sessiz.

          Tuval bunu ayrıca yazmış: *"NULL: hesaplanamadı. %0 ile AYNI
          GÖRÜNMEMELİ."* Çözümü de tuvalden: ölçülemeyen basamağın yolu
          **kesik çizgili**, dolu değil — bakınca "burada bir şey
          ölçülmedi" diyor.

          İki cümle farklı iş emri veriyor: *kimse geçemedi* = huniyi
          düzelt. *Hesaplanamadı* = önceki basamakta hiç aday yok, önce
          oraya bak.
        */}
        <ol className="flex flex-col gap-2">
          {data.huni.map((s) => {
            const olculemedi = s.gecisOrani === null;
            return (
              <li key={s.basamak} className="flex items-center gap-3">
                <span className="text-notr-700 w-40 shrink-0 text-sm">
                  {BASAMAK_ADI[s.basamak] ?? s.basamak}
                </span>
                <span
                  className={
                    olculemedi
                      ? "rounded-alan border-notr-300 h-6 flex-1 overflow-hidden border border-dashed"
                      : "rounded-alan bg-notr-100 h-6 flex-1 overflow-hidden"
                  }
                >
                  <span
                    className="bg-marka-600 block h-full"
                    style={{ width: `${enGenis === 0 ? 0 : (s.ulasan / enGenis) * 100}%` }}
                  />
                </span>
                <span className="text-notr-900 w-12 shrink-0 text-right text-sm font-semibold tabular-nums">
                  {s.ulasan}
                </span>
                <span className="text-notr-600 w-14 shrink-0 text-right text-xs tabular-nums">
                  <span>{oran(s.gecisOrani)}</span>
                  {/*
                    `—` sessiz kalmasın: kısa sütuna sığmayan cümle
                    ekran okuyucuya söyleniyor. Gözle okuyan aynı bilgiyi
                    kesik çizgili yoldan ve alttaki açıklamadan alıyor.
                  */}
                  {olculemedi && <span className="sr-only">hesaplanamadı</span>}
                </span>
              </li>
            );
          })}
        </ol>

        {/*
          Açıklama YALNIZCA kesik çizgili satır varken çiziliyor. Her
          zaman durursa okunmaz hâle gelir ve okunmayan açıklama,
          olmayan açıklamadır.
        */}
        {data.huni.some((s) => s.gecisOrani === null) && (
          <p className="text-notr-600 mt-2 text-xs">
            Kesik çizgili basamakların geçiş oranı <strong>hesaplanamadı</strong>: bir önceki
            basamakta hiç aday yok. Bu, kimsenin geçemediği anlamına gelmez.
          </p>
        )}

        <p className="text-notr-700 mt-3 text-sm">
          Genel dönüşüm: <strong>{oran(data.genelDonusum)}</strong> ({data.toplamAday} aday)
        </p>
      </Kart>

      <Kart baslik="Kaynaklar">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <caption className="sr-only">Kaynak bazında aday, kayıt ve maliyet</caption>
            <thead>
              <tr className="border-notr-200 text-notr-600 border-b text-left text-xs">
                <th scope="col" className="py-2 pr-3 font-medium">
                  Kaynak
                </th>
                <th scope="col" className="py-2 pr-3 text-right font-medium">
                  Aday
                </th>
                <th scope="col" className="py-2 pr-3 text-right font-medium">
                  Kayıt
                </th>
                <th scope="col" className="py-2 pr-3 text-right font-medium">
                  Dönüşüm
                </th>
                <th scope="col" className="py-2 text-right font-medium">
                  Kayıt başı maliyet
                </th>
              </tr>
            </thead>
            <tbody>
              {data.kaynaklar.map((k) => (
                <tr key={k.kaynak} className="border-notr-100 border-b last:border-0">
                  <td className="text-notr-900 py-2 pr-3">{KAYNAK_ADI[k.kaynak]}</td>
                  <td className="py-2 pr-3 text-right tabular-nums">{k.aday}</td>
                  <td className="py-2 pr-3 text-right tabular-nums">{k.kayit}</td>
                  <td className="py-2 pr-3 text-right tabular-nums">{oran(k.donusum)}</td>
                  <td className="py-2 text-right">
                    <MaliyetHucresi maliyet={k.maliyet} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Kart>

      {data.kayipNedenleri.length > 0 && (
        <Kart baslik="Kaybedilme nedenleri">
          <ul className="flex flex-col gap-1 text-sm">
            {data.kayipNedenleri.map((n) => (
              <li key={n.neden ?? "yok"} className="flex justify-between">
                <span className="text-notr-700">
                  {n.neden ? NEDEN_ADI[n.neden] : "Belirtilmemiş"}
                </span>
                <span className="text-notr-900 font-medium tabular-nums">{n.sayi}</span>
              </li>
            ))}
          </ul>
        </Kart>
      )}
    </div>
  );
}

/**
 * ================================================================
 * OLCULMEMIS MALIYET "0 ₺" DEGIL "—"
 * ================================================================
 * Iki ayri "olculmedi" hali var ve ikisi FARKLI sey soyluyor:
 *   HARCAMA_GIRILMEDI — bu kanala ne harcandigi bilinmiyor
 *   KAYIT_YOK         — para harcandi ama henuz kayit gelmedi
 * Ikincisinde harcama TUTARI gosteriliyor: raporun en degerli satiri
 * "8.000 ₺ harcandi, kayit yok" satiridir ve "—" ile gizlenirse fark
 * edilmez.
 */
function MaliyetHucresi({ maliyet }: { maliyet: Maliyet }) {
  if (maliyet.olculdu) {
    return <span className="font-medium tabular-nums">{para(maliyet.kayitBasina)}</span>;
  }

  if (maliyet.neden === "KAYIT_YOK" && maliyet.harcama !== null) {
    return (
      <span className="text-notr-600">
        <span className="tabular-nums">{para(maliyet.harcama)}</span>
        <span className="text-uyari-600 block text-xs">harcandı, kayıt yok</span>
      </span>
    );
  }

  return (
    <span className="text-notr-600">
      —<span className="block text-xs">harcama girilmedi</span>
    </span>
  );
}

/**
 * TANIŞMA GÖRÜŞMESİ PLANLAMA — F-CRM-04, matris §5.2
 *
 * `POST /crm/gorusmeler` yazılıydı, hiçbir ekran çağırmıyordu. Adayla
 * görüşme yapılıp raporu YAZILABİLİYOR ama görüşmenin kendisi
 * PLANLANAMIYORDU: satış danışmanı bir adayı uzmana yönlendirmek
 * istediğinde panelde yapabileceği bir şey yoktu.
 *
 * ================================================================
 * UZMAN LİSTEDEN SEÇİLİYOR, KİMLİK YAZILMIYOR
 * ================================================================
 * Personel listesi kapsam süzgeçli geliyor. Rol koduna göre
 * süzmüyoruz — `roller` alanı görüntüleme içindir, yetki kararı
 * değildir ve rol adına göre dallanmak bu projede yasak. Kimin
 * görüşme yapabileceğine sunucu karar veriyor; yanlış kişi
 * seçilirse uç reddediyor ve hatası gösteriliyor.
 */
function GorusmePlanla({ adayId }: { adayId: string }) {
  const istemci = useQueryClient();
  const [acik, setAcik] = useState(false);
  const [coachId, setCoachId] = useState("");
  const [tarih, setTarih] = useState(() => yerelGunBasi().toISOString().slice(0, 10));
  const [saat, setSaat] = useState("14:00");
  const [sure, setSure] = useState("45");
  const [gundem, setGundem] = useState("");
  const [planlanan, setPlanlanan] = useState<string | null>(null);

  /*
    ================================================================
    PERSONEL YONETIM LISTESI DEGIL, SECIM LISTESI — 07.09.2026
    ================================================================
    Burasi `personelListesi(false)` cagiriyordu; o uc
    `kisi.personel.goruntule` istiyor ve SATIS DANISMANINDA YOK.
    Sonuc: "Tanisma gorusmesi planla" dugmesine basan satis danismani
    "Personel listesi alinamadi; gorusme planlanamiyor" yazisini
    goruyor ve akis kapaniyordu — matris §5.2 ona bu yetkiyi
    verdigi halde.

    `gorusme-uzmanlari` yalnizca kimlik ve ad donduruyor; korumasi
    isin kendi izni (`crm.gorusme.planla`).
  */
  const personel = useQuery({
    queryKey: ["gorusme-uzmanlari"],
    queryFn: gorusmeUzmanlari,
    retry: false,
  });

  const mut = useMutation({
    mutationFn: () =>
      gorusmePlanla({
        leadId: adayId,
        coachId,
        scheduledAt: `${tarih}T${saat}:00`,
        durationMin: Number(sure),
        agenda: gundem.trim() || null,
      }),
    onSuccess: async (c) => {
      setPlanlanan(c.scheduledAt);
      await istemci.invalidateQueries({ queryKey: ["aday", adayId] });
    },
  });

  if (!acik) {
    return (
      <div className="border-notr-200 border-t pt-4">
        <Buton gorunum="ikincil" onClick={() => setAcik(true)}>
          Tanışma görüşmesi planla
        </Buton>
      </div>
    );
  }

  return (
    <section className="border-notr-200 flex flex-col gap-3 border-t pt-4">
      <h4 className="text-notr-900 text-sm font-semibold">Tanışma görüşmesi</h4>

      {planlanan !== null ? (
        <>
          <p className="text-notr-700 text-sm">
            Görüşme <strong>{anMetni(planlanan)}</strong> için planlandı. Uzmanın takviminde
            görünecek.
          </p>
          <div>
            <Buton
              gorunum="ikincil"
              onClick={() => {
                setPlanlanan(null);
                setAcik(false);
              }}
            >
              Kapat
            </Buton>
          </div>
        </>
      ) : personel.isPending ? (
        <Iskelet satir={2} />
      ) : personel.isError ? (
        <p className="text-notr-600 text-sm">Personel listesi alınamadı; görüşme planlanamıyor.</p>
      ) : (
        <>
          <Secim
            etiket="Kim görüşecek"
            value={coachId}
            onChange={(e) => setCoachId(e.target.value)}
          >
            <option value="">Seçiniz</option>
            {personel.data.map((u) => (
              <option key={u.id} value={u.id}>
                {u.ad}
              </option>
            ))}
          </Secim>

          <div className="grid gap-3 sm:grid-cols-3">
            <Alan
              etiket="Tarih"
              type="date"
              value={tarih}
              onChange={(e) => setTarih(e.target.value)}
            />
            <Alan
              etiket="Saat"
              type="time"
              value={saat}
              onChange={(e) => setSaat(e.target.value)}
            />
            <Alan
              etiket="Süre (dk)"
              type="number"
              min={5}
              max={480}
              value={sure}
              onChange={(e) => setSure(e.target.value)}
            />
          </div>

          <Alan
            etiket="Gündem (isteğe bağlı)"
            value={gundem}
            onChange={(e) => setGundem(e.target.value)}
            placeholder="Neye odaklanılacak"
          />

          {mut.isError && (
            <p role="alert" className="text-hata-700 text-sm font-medium">
              {mut.error instanceof ApiHatasi ? mut.error.message : "Görüşme planlanamadı."}
            </p>
          )}

          <div className="flex flex-wrap gap-2">
            <Buton
              yukleniyor={mut.isPending}
              disabled={coachId === ""}
              onClick={() => mut.mutate()}
            >
              Görüşmeyi planla
            </Buton>
            <Buton gorunum="ikincil" onClick={() => setAcik(false)}>
              Vazgeç
            </Buton>
          </div>
        </>
      )}
    </section>
  );
}
